import { useSearchTerm } from "@/hooks/use-search-term";
import { useBlogsData } from "@/hooks/use-blogs-data";
import { useLanguage } from "@/contexts/LanguageContext";
import Layout from "@/components/layouts/Layout";
import SearchBarSection from "@/components/sections/SearchBarSection";
import BlogResultsSection from "@/components/features/blogs/BlogResultsSection";
import CategoryTabSection from "@/components/features/blogs/CategoryTabSection";
import NewsCTASection from "@/components/sections/NewsCTASection";
import Pagination from "@/components/ui/pagination";
import BlogSkeleton from "@/components/loaders/BlogSkeleton";
import { Tabs, TabsContent } from "@/components/ui/tabs";

const itemsPerPage = 9;

const Blog = () => {
  const { t, language } = useLanguage();
  const { searchTerm, setSearchTerm } = useSearchTerm(200);

  const {
    blogs,
    categories,
    loading,
    error,
    selectedCategoryId,
    currentPage,
    totalPosts,
    setCurrentPage,
    handleCategoryChange,
  } = useBlogsData(itemsPerPage);

  const filteredBlogs = blogs.filter((blog) => {
    const title = blog.title?.toLowerCase?.() || "";
    const excerpt = typeof blog.excerpt === "string" ? blog.excerpt.toLowerCase() : "";
    const content = typeof blog.content === "string" ? blog.content.toLowerCase() : "";
    return (
      title.includes(searchTerm.toLowerCase()) ||
      excerpt.includes(searchTerm.toLowerCase()) ||
      content.includes(searchTerm.toLowerCase())
    );
  });

  const tabItems = [
    { id: "All", label: language === "ar" ? "الكل" : "All" },
    ...categories.map((cat) => ({ id: cat.id, label: cat.title })),
  ];

  if (error) {
    return (
      <Layout>
        <div className="pt-24 text-center text-red-700 dark:text-red-300">
          <h1 className="mb-4 text-3xl font-bold text-nexus-gray dark:text-white">
            {t("blog_screen_title")}
          </h1>
          {language === "ar" ? `\u062e\u0637\u0623: ${error}` : `Error: ${error}`}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="pt-24 pb-16 bg-gradient-to-b from-white to-gray-50 dark:from-nexus-dark dark:to-gray-900">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-nexus-gray dark:text-white mb-6">
              {t("blog_screen_title")}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              {t("blog_screen_descrition")}
            </p>
            <SearchBarSection
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder={t("blog_search_bar")}
            />
          </div>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-nexus-dark">
        <div className="container">
          {loading ? (
            <BlogSkeleton />
          ) : (
            <>
              <Tabs
                value={selectedCategoryId}
                onValueChange={(val) => {
                  handleCategoryChange(val);
                  setSearchTerm("");
                }}
              >
                <CategoryTabSection
                  categories={tabItems}
                  selectedCategoryId={selectedCategoryId}
                  onSelectCategory={handleCategoryChange}
                  language={language}
                />
                <TabsContent value={selectedCategoryId}>
                  {filteredBlogs.length > 0 ? (
                    <BlogResultsSection
                      blogs={filteredBlogs}
                      totalCount={totalPosts}
                      searchTerm={searchTerm}
                      selectedCategory={
                        tabItems.find((item) => item.id === selectedCategoryId)?.label ||
                        t("blog_category")
                      }
                      onClearFilters={() => {
                        setSearchTerm("");
                        handleCategoryChange("All");
                      }}
                    />
                  ) : (
                    <div className="text-center text-gray-500 dark:text-gray-300">
                      {t("no_blog_posts_found")}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <Pagination
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                totalPages={Math.ceil(totalPosts / itemsPerPage)}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </>
          )}
        </div>
      </section>

      <NewsCTASection />
    </Layout>
  );
};

export default Blog;
